import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plus } from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  useV1ClassesList,
  useV1SchoolsList,
  useV1MeRetrieve,
} from '@/api/client'
import { V1ClassesListState, V1SchoolsListState } from '@/api/model'
import type { ClassList, StateEnum } from '@/api/model'
import { OpenProgramToggle } from '@/components/open-program-toggle'
import { PageHeader } from '@/components/page-header'
import { StatusBadge, type StatusTone } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable, type Column, type StateOption } from '@/components/data-table'
import { LookupTruncationNote } from '@/components/lookup-truncation-note'
import { ClassForm } from '@/components/classes/class-form'
import { queryHasHttpError } from '@/lib/query-state'

type KlasySearch = { school?: number }

export const Route = createFileRoute('/_panel/klasy/')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && !['teacher', 'admin', 'superadmin'].includes(me.data.role)) {
      throw redirect({ to: '/' })
    }
  },
  // Accept `?school=<id>` so the school-detail "Zobacz klasy" deep-link lands the
  // list pre-filtered to that school (spec 012 FR-036 / clarification Q1).
  validateSearch: (search: Record<string, unknown>): KlasySearch => {
    const school = Number(search.school)
    return Number.isFinite(school) && school > 0 ? { school } : {}
  },
  component: KlasyPage,
})

const PAGE_SIZE = 25
const ALL_SCHOOLS = 'all'

const stateOptions: StateOption<V1ClassesListState>[] = [
  { value: V1ClassesListState.active, label: 'Aktywne' },
  { value: V1ClassesListState.archived, label: 'Zarchiwizowane' },
  { value: V1ClassesListState.all, label: 'Wszystkie' },
]

const stateTone: Record<StateEnum, StatusTone> = {
  active: 'green',
  archived: 'zinc',
  deleted: 'red',
}

function KlasyPage() {
  const navigate = useNavigate()
  const meQuery = useV1MeRetrieve()
  const role = meQuery.data?.status === 200 ? meQuery.data.data.role : undefined
  const isOperator = role === 'admin' || role === 'superadmin'
  const isTeacher = role === 'teacher'

  const { school: schoolParam } = Route.useSearch()
  const [creating, setCreating] = useState(false)
  const [page, setPage] = useState(1)
  const [showOpenProgram, setShowOpenProgram] = useState(Boolean(schoolParam))
  const [search, setSearch] = useState('')
  const [state, setState] = useState<V1ClassesListState>(V1ClassesListState.active)
  const [school, setSchool] = useState<string>(schoolParam ? String(schoolParam) : ALL_SCHOOLS)
  const [ordering, setOrdering] = useState('school__name')

  const schoolsQuery = useV1SchoolsList(
    {
      state: V1SchoolsListState.active,
      page_size: 100,
      is_open_program: showOpenProgram ? undefined : false,
    },
    { query: { enabled: isOperator } },
  )
  const schools = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data.results : []
  const schoolsCount = schoolsQuery.data?.status === 200 ? schoolsQuery.data.data.count : 0

  const query = useV1ClassesList({
    state,
    school_is_open_program: isTeacher || showOpenProgram ? undefined : false,
    school: school === ALL_SCHOOLS ? undefined : Number(school),
    search: search || undefined,
    ordering: ordering || undefined,
    page,
    page_size: PAGE_SIZE,
  })
  const data = query.data?.status === 200 ? query.data.data : undefined

  if (creating && isOperator) {
    return (
      <>
        <PageHeader
          title={
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" /> Klasy
            </button>
          }
        />
        <div className="p-6">
          <h2 className="mb-6 text-lg font-semibold">Nowa klasa</h2>
          <ClassForm />
        </div>
      </>
    )
  }

  const columns: Column<ClassList>[] = [
    {
      header: 'Klasa',
      sortKey: 'name',
      cell: (c) => <span className="font-medium">{c.name}</span>,
    },
    {
      header: 'Szkoła',
      sortKey: 'school__name',
      cell: (c) => (
        <span className="text-muted-foreground">
          {c.school_name}
          <span className="ml-1.5 text-xs">{c.school_city}</span>
        </span>
      ),
    },
    {
      header: 'Rok startu',
      sortKey: 'program_start_year',
      cell: (c) => <span className="text-muted-foreground">{c.program_start_year}</span>,
    },
    {
      header: 'Data dołączenia',
      sortKey: 'joined_program_on',
      cell: (c) => <span className="text-muted-foreground">{c.joined_program_on}</span>,
    },
    {
      header: 'Profil',
      cell: (c) => <span className="text-muted-foreground">{c.profile || '—'}</span>,
    },
    {
      // Not sortable: student_count is an honest-null placeholder (no Student
      // entity yet — spec 012 DR-003), so the backend never orders by it.
      header: 'Uczniowie',
      className: 'text-right',
      cell: (c) => (
        <span className="tabular-nums text-muted-foreground">{c.student_count ?? '—'}</span>
      ),
    },
    {
      header: 'Stan',
      sortKey: 'state',
      cell: (c) => <StatusBadge tone={stateTone[c.state]}>{c.state_label}</StatusBadge>,
    },
  ]

  return (
    <>
      <PageHeader
        title={isTeacher ? 'Moje klasy' : 'Klasy'}
        actions={
          isOperator && (
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus /> Załóż klasę
            </Button>
          )
        }
      />
      <div className="p-6">
        <DataTable
          columns={columns}
          rows={data?.results ?? []}
          rowKey={(c) => c.id}
          count={data?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={PAGE_SIZE}
          search={search}
          onSearchChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          searchPlaceholder="Szukaj klasy…"
          stateValue={state}
          stateOptions={stateOptions}
          onStateChange={(v) => {
            setState(v)
            setPage(1)
          }}
          ordering={ordering}
          onOrderingChange={(v) => {
            setOrdering(v)
            setPage(1)
          }}
          filters={
            isOperator && (
              <>
                <OpenProgramToggle
                  checked={showOpenProgram}
                  onCheckedChange={(value) => {
                    setShowOpenProgram(value)
                    if (!value) setSchool(ALL_SCHOOLS)
                    setPage(1)
                  }}
                />
                <Select
                  value={school}
                  onValueChange={(v) => {
                    setSchool(v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger size="sm" className="w-52">
                    <SelectValue placeholder="Wszystkie szkoły" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_SCHOOLS}>Wszystkie szkoły</SelectItem>
                    {schools.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                    <LookupTruncationNote count={schoolsCount} shown={schools.length} />
                  </SelectContent>
                </Select>
              </>
            )
          }
          isLoading={query.isLoading}
          isError={queryHasHttpError(query)}
          onRetry={() => void query.refetch()}
          emptyMessage="Brak klas spełniających kryteria."
          onRowClick={(c) => navigate({ to: '/klasy/$klasaId', params: { klasaId: String(c.id) } })}
          countNoun={['klasa', 'klas']}
        />
      </div>
    </>
  )
}
